import * as React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Grid,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { ImagesContext } from "../Context";

interface Props {
  searchPatients: any;
  setSearchPatients: any;
  selectedPatients: any;
  setSelectedPatients: any;
  setOnList: any;
  setPendingAdd: any;
  setPendingRemoval: any;
  onList: any;
  pendingAdd: any;
  pendingRemoval: any;
  paramsPresent: boolean;
  loading: boolean;
  currentPage: number;
  setCurrentPage: Function;
  totalPages: number;
  sortObject: any;
  setSortObject: any;
}

const preprocessPatients = (patients) => {
  return patients.map((patient) => ({
    ...patient,
    active_program_titles_string: patient.active_program_titles
      ? patient.active_program_titles.join(", ")
      : "",
    diagnosis_code_values_string: patient.diagnosis_code_values
      ? patient.diagnosis_code_values.join(", ")
      : "",
    ltc_facility_names_string: patient.ltc_facility_names
      ? patient.ltc_facility_names.join(", ")
      : "",
    insurance_types_list_string: patient.insurance_types_list
      ? patient.insurance_types_list.join(", ")
      : "",
  }));
};

const SearchResultsWidget: React.FC<Props> = (props: any) => {
  const images = React.useContext(ImagesContext);

  const setSortOrder = (sortBy, direction) => {
    let sort = {
      field: sortBy,
      direction: direction,
    };
    props.setSortObject(sort);
  };

  const displayArrayField = (arrayField) => {
    return arrayField && arrayField.length > 0 ? arrayField.join(", ") : "";
  };

  const removeFromStatusList = (listType: string, user: any) => {
    switch (listType) {
      case "Pending Add": {
        const filteredPendingAdd = props.pendingAdd.filter(
          (u) => u.id !== user.id
        );
        props.setPendingAdd([...filteredPendingAdd]);
        break;
      }
      case "Pending Removal": {
        const filteredPendingRemoval = props.pendingRemoval.filter(
          (u) => u.id !== user.id
        );
        props.setPendingRemoval([...filteredPendingRemoval]);
        break;
      }
      case "On List": {
        const filteredOnList = props.onList.filter((u) => u.id !== user.id);
        props.setOnList([...filteredOnList]);
        break;
      }
    }
  };

  const addToStatusList = (listType, user) => {
    switch (listType) {
      case "Pending Add":
        user.list_status = "Pending Add";
        props.setPendingAdd([...props.pendingAdd, user]);
        break;

      case "Pending Removal":
        user.list_status = "Pending Removal";
        props.setPendingRemoval([...props.pendingRemoval, user]);
        break;

      case "On List":
        user.list_status = "On List";
        props.setOnList([...props.onList, user]);
        break;
    }
  };

  const handlePatientSelection = (checked: boolean, user) => {
    if (checked) {
      props.setSelectedPatients([...props.selectedPatients, user]);
      removeFromStatusList(user.list_status, user); //remove from old lists
      const status = user.on_list ? "On List" : "Pending Add";
      addToStatusList(status, user); //add to new lists
      const updatedPatients = props.searchPatients.map((patient) =>
        patient.id === user.id ? { ...patient, list_status: status } : patient
      );
      props.setSearchPatients(updatedPatients);
    } else {
      let filteredList = props.selectedPatients.filter((u) => u.id != user.id);
      props.setSelectedPatients([...filteredList]);
      removeFromStatusList(user.list_status, user); //remove from old lists
      const status = user.on_list ? "Pending Removal" : "Not on List";
      addToStatusList(status, user); //add to new lists
      const updatedPatients = props.searchPatients.map((patient) =>
        patient.id === user.id ? { ...patient, list_status: status } : patient
      );
      props.setSearchPatients(updatedPatients);
    }
  };

  const selectAll = (checked) => {
    let searchPatients = props.searchPatients;
    let pendingAdd = props.pendingAdd;
    let pendingRemoval = props.pendingRemoval;
    let onList = props.onList;

    props.searchPatients.forEach((user) => {
      if (checked) {
        // remove from old list
        switch (user.list_status) {
          case "Pending Add":
            pendingAdd = pendingAdd.filter((u) => u.id !== user.id);
            break;
          case "Pending Removal":
            pendingRemoval = pendingRemoval.filter((u) => u.id !== user.id);
            break;
          case "On List":
            onList = onList.filter((u) => u.id !== user.id);
            break;
        }

        const status = user.on_list ? "On List" : "Pending Add";

        // add to new list
        switch (status as string) {
          case "Pending Add":
            pendingAdd = [...pendingAdd, user];
            break;

          case "Pending Removal":
            pendingRemoval = [...pendingRemoval, user];
            break;

          case "On List":
            onList = [...onList, user];
            break;
        }

        // update status on user
        searchPatients = searchPatients.map((patient) =>
          patient.id === user.id ? { ...patient, list_status: status } : patient
        );
      } else {
        // remove from old list
        switch (user.list_status) {
          case "Pending Add":
            pendingAdd = pendingAdd.filter((u) => u.id !== user.id);
            break;
          case "Pending Removal":
            pendingRemoval = pendingRemoval.filter((u) => u.id !== user.id);
            break;
          case "On List":
            onList = onList.filter((u) => u.id !== user.id);
            break;
        }

        const status = user.on_list ? "Pending Removal" : "Not on List";

        // add to new list
        switch (status as string) {
          case "Pending Add":
            pendingAdd = [...pendingAdd, user];
            break;

          case "Pending Removal":
            pendingRemoval = [...pendingRemoval, user];
            break;

          case "On List":
            onList = [...onList, user];
            break;
        }

        // update status on user
        searchPatients = searchPatients.map((patient) =>
          patient.id === user.id ? { ...patient, list_status: status } : patient
        );
      }

      props.setSearchPatients(searchPatients);
      props.setPendingAdd(pendingAdd);
      props.setPendingRemoval(pendingRemoval);
      props.setOnList(onList);
    });

    if (checked) {
      props.setSelectedPatients([
        ...props.selectedPatients,
        ...props.searchPatients,
      ]);
    } else {
      let filteredList = [...props.selectedPatients];
      props.searchPatients.forEach((patient) => {
        filteredList = filteredList.filter((u) => u.id != patient.id);
      });
      props.setSelectedPatients([...filteredList]);
    }
  };

  const isUserChecked = (user) => {
    let filtered = props.selectedPatients.filter(
      (patient) => patient.id == user.id
    );
    return filtered.length > 0;
  };

  const formatPatientNameReversed = (firstName, middleName, lastName) => {
    const capitalize = (s) => {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    };

    const formattedFirstName = capitalize(firstName);
    const formattedLastName = capitalize(lastName);

    let formattedMiddleInitial = "";
    if (middleName && middleName.trim() !== "") {
      formattedMiddleInitial = capitalize(middleName.charAt(0));
    }

    return `${formattedLastName}, ${formattedFirstName} ${formattedMiddleInitial}`.trim();
  };

  React.useEffect(() => {
    if (props.searchPatients) {
      const preprocessedPatients = preprocessPatients(props.searchPatients);
      if (
        JSON.stringify(preprocessedPatients) !==
        JSON.stringify(props.searchPatients)
      ) {
        props.setSearchPatients(preprocessedPatients);
      }
    }
  }, [props.searchPatients]);

  const renderSearchTitle = () => {
    if (!props.paramsPresent && props.pendingAdd.length > 0) {
      return <h3> Current Pending </h3>;
    } else if (!props.paramsPresent) {
      return <h3> Current List </h3>;
    } else {
      return (
        <>
          <h3>Search Results</h3>
          <div className="badge">
            {props.paramsPresent && props.searchPatients
              ? props.searchPatients.length
              : "-"}
          </div>
        </>
      );
    }
  };

  return (
    <Grid item xs={12}>
      <Grid container>
        <Grid
          container
          justifyContent="space-between"
          className="widget-container"
          sx={{ marginRight: "0!important" }}
        >
          <Grid item xs={12} className="widget-header">
            {renderSearchTitle()}
          </Grid>
        </Grid>
        <div className="divider"></div>
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{ marginRight: "0!important" }}
            className="widget-body content-center"
          >
            {props.loading ? (
              <div className="loading-container">
                <img
                  src={images.new_large_spinner_src}
                  alt="Loading spinner"
                ></img>
                <h3>Loading...</h3>
                <p>
                  We are loading your patients list.
                  <br />
                  This might take a few minutes. Please do not leave this page.
                </p>
              </div>
            ) : props.searchPatients.length > 0 ? (
              <div className="search-table-container">
                <TableContainer component={Paper}>
                  <Table
                    stickyHeader
                    className="no-border-table"
                    aria-label="simple table"
                    style={{ minWidth: "1600px" }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className="table-header"
                          style={{
                            cursor: "pointer",
                            width: "0px",
                            textAlign: "center",
                          }}
                        >
                          <div style={{ fontSize: "10px" }}>
                            <b>Select All</b>
                          </div>
                          <input
                            className="user-selection orange-checkbox"
                            type="checkbox"
                            name="selectAll"
                            onChange={(event) => {
                              selectAll(event.target.checked);
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{
                            cursor: "pointer",
                            width: "242px",
                            padding: "0px 12px",
                          }}
                          className="table-header name-header"
                        >
                          <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Patients Name
                          </strong>
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{
                            cursor: "default",
                            width: "242px",
                            padding: "0px 12px",
                          }}
                          className="table-header"
                        >
                          <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Program(s)
                          </strong>
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{
                            cursor: "default",
                            width: "242px",
                            padding: "0px 12px",
                          }}
                          className="table-header"
                        >
                          <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Diagnosis Code(s)
                          </strong>
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{
                            cursor: "default",
                            width: "242px",
                            padding: "0px 12px",
                          }}
                          className="table-header"
                        >
                          <strong style={{ fontFamily: "QuicksandMedium" }}>
                            Insurance Type(s)
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <>
                        {props.searchPatients?.map((user, index) => (
                          <TableRow
                            key={index + 1}
                            className={index % 2 == 0 ? "rowEven" : "row"}
                          >
                            <TableCell
                              size="small"
                              scope="row"
                              align="center"
                              className="tableCell"
                            >
                              <input
                                className="user-selection orange-checkbox"
                                type="checkbox"
                                name="selectCustomer"
                                onChange={(event) => {
                                  handlePatientSelection(
                                    event.target.checked,
                                    user
                                  );
                                }}
                                checked={isUserChecked(user)}
                              />
                            </TableCell>
                            <TableCell
                              size="small"
                              scope="row"
                              align="left"
                              className="tableCell"
                            >
                              {formatPatientNameReversed(
                                user.first_name,
                                user.middle_name,
                                user.last_name
                              )}
                            </TableCell>
                            <TableCell
                              size="small"
                              scope="row"
                              align="left"
                              className="tableCell"
                            >
                              {displayArrayField(user.active_program_titles)}
                            </TableCell>
                            <TableCell
                              size="small"
                              scope="row"
                              align="left"
                              className="tableCell"
                            >
                              {displayArrayField(user.diagnosis_code_values)}
                            </TableCell>
                            <TableCell
                              size="small"
                              scope="row"
                              align="left"
                              className="tableCell"
                            >
                              {displayArrayField(user.insurance_types_list)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    </TableBody>
                  </Table>
                </TableContainer>
                {props.totalPages > 0 && (
                  <Pagination
                    count={props.totalPages}
                    page={props.currentPage}
                    onChange={(_event: React.ChangeEvent, page: number) =>
                      props.setCurrentPage(page)
                    }
                    sx={{ margin: "0 auto", paddingTop: "16px" }}
                    boundaryCount={3}
                  />
                )}
              </div>
            ) : (
              <div>To search for a patient, enter criteria in the fields</div>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SearchResultsWidget;
